import type { Theme } from '@mui/material';
import { Button, Card, CardHeader, Divider, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import AssignAcpToDestinationDialog from '../../organisms/dialogs/assign-access-control-profiles-to-destination-dialog';

import { AccessControlProfile } from '@/features/access-control-profiles/api/types/access-control-profile';
import { useDialog } from '@/hooks/use-dialog';

interface DestinationACSProps {
  id: number;
  accessControlProfiles: AccessControlProfile[];
}

export const DestinationACS: FC<DestinationACSProps> = props => {
  const { id, accessControlProfiles, ...other } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';
  const { t } = useTranslation(['destination', 'common']);

  const [
    isOpenAssignAccessControlProfilesToDestinationDialog,
    handleOpenAssignAccessControlProfilesToDestinationDialog,
    handleCloseAssignAccessControlProfilesToDestinationDialog,
  ] = useDialog();

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:profiles.cardTitle')}
          action={
            <Button
              onClick={handleOpenAssignAccessControlProfilesToDestinationDialog}
              color="primary"
              variant="text"
              size="large">
              {t('common:button.edit')}
            </Button>
          }
        />
        <Divider />
        <PropertyList>
          {accessControlProfiles.length > 0 ? (
            accessControlProfiles.map((acs, i) => (
              <PropertyListItem
                key={acs.id}
                align={align}
                label={`${t('destination:profiles.profile')} #${i + 1}`}
                value={acs.name}
                width={200}
              />
            ))
          ) : (
            <Typography sx={{ p: 3 }} variant="inherit">
              {t('destination:profiles.noAccess')}
            </Typography>
          )}
        </PropertyList>
      </Card>

      <AssignAcpToDestinationDialog
        open={isOpenAssignAccessControlProfilesToDestinationDialog}
        onCancel={handleCloseAssignAccessControlProfilesToDestinationDialog}
        destinationId={id}
        accessControlProfilesIds={accessControlProfiles.map(acp => acp.id)}
      />
    </>
  );
};
